export const marketingCaseStudyPages = [
  {
    slug: "stair-warehouse",
    client: "Stair Warehouse",
    title: "How We Helped Stair Warehouse Generate $412,933 Through Klaviyo",
    description:
      "We managed Stair Warehouse’s Klaviyo email program, which generated $412,932.88 in attributed revenue over six months.",
    metaDescription:
      "We managed Stair Warehouse’s Klaviyo email program, which generated $412,932.88 in attributed revenue over six months.",
    stats: [
      {
        value: "$412,933",
        label: "Klaviyo-attributed revenue",
      },
      {
        value: "6 months",
        label: "Reporting window",
      },
      {
        value: "$357,525",
        label: "Abandoned-cart revenue",
      },
      {
        value: "$47,753",
        label: "Welcome-series revenue",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "Automated email became a consistent source of attributed sales.",
      paragraphs: [
        "Stair Warehouse sells products that customers may research before completing a purchase. An abandoned cart or an initial visit can be part of that buying process rather than the end of it.",
        "We managed its Klaviyo lifecycle program so shoppers received relevant messages after visiting the store, browsing products, or leaving a cart. We tracked performance over the six months leading up to March 11, 2026.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "The program needed to follow up with shoppers without depending on frequent campaigns.",
      paragraphs: [
        "Cart abandonment and browsing activity created opportunities to continue the conversation. The challenge was to make those messages useful at different stages of the customer journey.",
        "We needed to improve abandoned-cart open rates and develop a more regular campaign calendar while maintaining the automated flows that already generated sales.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading:
        "We managed the flows around customer intent and reviewed their performance.",
      paragraphs: [
        "We managed abandoned-cart messages, browse abandonment, the welcome series, and post-purchase review requests. We tailored each flow to the shopper’s stage in the buying journey.",
        "We reviewed attributed sales, engagement, placed orders, and deliverability to identify improvements. We also recommended campaigns for professional buyers interested in new products.",
      ],
      highlights: [
        "We managed abandoned-cart and welcome email flows.",
        "We supported browse-abandonment and post-purchase messages.",
        "We reviewed flow engagement and placed-order performance.",
        "We reviewed deliverability and identified further improvements.",
        "We recommended targeted campaigns for professional buyers.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading: "Klaviyo attributed $412,932.88 to email over six months.",
      paragraphs: [
        "Automated flows accounted for $412,104.41 of the total. The abandoned-cart flow contributed $357,524.68 and the welcome series contributed $47,752.54.",
        "Browse abandonment added $4,508.07 and post-purchase review messages added $2,319.12. The result demonstrates the value of maintaining several customer follow-up paths rather than relying on one campaign.",
      ],
      followUp:
        "Klaviyo attributed $412,104.41 to automated flows and $412,932.88 to email overall during this six-month period.",
    },
    services: [
      "Klaviyo flow management",
      "Lifecycle email marketing",
      "Email performance reporting",
      "Deliverability review",
      "Customer segmentation planning",
    ],
    exhibit: {
      title: "Revenue from lifecycle email",
      period: "Six months ending March 11, 2026",
      unit: "Klaviyo-attributed revenue",
      rows: [
        ["Abandoned cart", 357524.68],
        ["Welcome series", 47752.54],
        ["Browse abandonment", 4508.07],
        ["Post-purchase review", 2319.12],
      ],
      note: "Revenue uses Klaviyo attribution. Automated flows contributed $412,104.41 of the $412,932.88 email total.",
    },
    relatedServices: [
      {
        href: "/klaviyo-email-marketing-agency",
        label: "Klaviyo Email Marketing",
      },
      {
        href: "/email-marketing",
        label: "Ecommerce Email Marketing",
      },
    ],
    path: "/case-study/stair-warehouse",
    cta: {
      heading: "Ready to improve your email program?",
      description:
        "Tell us where your store or marketing needs attention. We can help define the work and the measures that matter.",
      primaryLabel: "Book a free call",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "gatehouse-supplies",
    client: "GateHouse Supplies",
    title:
      "How We Connected Email Marketing and BigCommerce Support for GateHouse Supplies",
    description:
      "We improved GateHouse Supplies’ BigCommerce storefront and Klaviyo emails, with $59,091 in email-attributed revenue in its February 2024 report.",
    metaDescription:
      "We improved GateHouse Supplies’ BigCommerce storefront and Klaviyo emails, with $59,091 in email-attributed revenue in its February 2024 report.",
    stats: [
      {
        value: "$59,091",
        label: "Klaviyo-attributed revenue",
      },
      {
        value: "$49,874",
        label: "Flow-attributed revenue",
      },
      {
        value: "$9,217",
        label: "Campaign-attributed revenue",
      },
      {
        value: "February 2024",
        label: "Email reporting period",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "A specialist parts business needed coordinated store and marketing support.",
      paragraphs: [
        "GateHouse Supplies operates a BigCommerce storefront with a catalog that depends on clear product identification and navigation. Customers need to find the right parts while returning buyers benefit from timely follow-up.",
        "We supported the BigCommerce storefront alongside its Klaviyo email program, combining catalog improvements with customer follow-up and regular performance reviews.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "Product discovery and customer follow-up both needed ongoing attention.",
      paragraphs: [
        "The brands page needed cleanup and better visibility. Detailed parts imagery helped customers identify components, while email flows needed refinement beyond their initial setup.",
        "The store also faced a decline in visits and average order value. A higher conversion rate alone could not explain the commercial picture, so our reporting kept orders and revenue in view.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading:
        "We improved email follow-up and made the catalog easier to navigate.",
      paragraphs: [
        "We adjusted the timing of the first high-value abandoned-cart email and added a second browse-abandonment follow-up. We also updated DMARC authentication and produced short-form and long-form email campaigns.",
        "On the storefront, we cleaned up the brands page and added footer links to make it easier to reach. We also produced an exploded parts view for the Doorking 6100 and reviewed remaining catalog issues with the client.",
      ],
      highlights: [
        "We refined abandoned-cart email timing.",
        "We added a browse-abandonment follow-up.",
        "We completed DMARC updates.",
        "We produced short-form and long-form campaigns.",
        "We cleaned up the brands page and added discovery links.",
        "We created a detailed exploded parts view.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading:
        "Our email program generated $59,091 in Klaviyo-attributed revenue.",
      paragraphs: [
        "In the February 2024 month-to-date report, Klaviyo attributed $49,874.30 to automated flows and $9,216.70 to campaigns. Together, they generated $59,091 in email-attributed revenue.",
        "Alongside the email program, we made the brands page easier to find and created detailed parts imagery to help customers navigate the catalog.",
      ],
      followUp:
        "We continued to support the catalog and refine customer follow-up through ongoing BigCommerce and Klaviyo work.",
    },
    services: [
      "BigCommerce storefront support",
      "Klaviyo flow optimization",
      "Email campaign production",
      "Email authentication updates",
      "Catalog merchandising",
      "Parts illustrations",
      "Performance reporting",
    ],
    exhibit: {
      title: "Automation and campaigns both contributed",
      period: "February 2024 email report",
      unit: "Klaviyo-attributed revenue",
      rows: [
        ["Automated flows", 49874.3],
        ["Email campaigns", 9216.7],
      ],
      note: "Revenue uses Klaviyo attribution from the February 2024 month-to-date report.",
    },
    relatedServices: [
      {
        href: "/bigcommerce-support-maintenance",
        label: "BigCommerce Support",
      },
      {
        href: "/klaviyo-email-marketing-agency",
        label: "Klaviyo Email Marketing",
      },
      {
        href: "/ecommerce-website-maintenance",
        label: "Ecommerce Maintenance",
      },
    ],
    path: "/case-study/gatehouse-supplies",
    cta: {
      heading: "Ready to discuss your ecommerce goals?",
      description:
        "Tell us where your store or marketing needs attention. We can help define the work and the measures that matter.",
      primaryLabel: "Book a free call",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "lummi-island-wild",
    client: "Lummi Island Wild",
    title: "How We Managed Google Ads at 923% ROAS for Lummi Island Wild",
    description:
      "We managed Lummi Island Wild’s Google Ads, generating $14,235.57 in attributed revenue from $1,541.49 in spend at 923% ROAS.",
    metaDescription:
      "We managed Lummi Island Wild’s Google Ads, generating $14,235.57 in attributed revenue from $1,541.49 in spend at 923% ROAS.",
    stats: [
      {
        value: "923%",
        label: "Google Ads ROAS",
      },
      {
        value: "$14,236",
        label: "Ads-reported revenue",
      },
      {
        value: "$1,541",
        label: "Advertising spend",
      },
      {
        value: "69",
        label: "Reported conversions",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading: "Paid-search performance needed to be measured against sales.",
      paragraphs: [
        "Lummi Island Wild’s ecommerce advertising required ongoing oversight of the searches reaching the account. Exposure alone was not the objective; the useful measure was the revenue Google Ads attributed to the advertising spend.",
        "We tracked August 1–20, 2026 performance alongside the latest seven-day results to guide account management.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "Not every seafood-related search represented the right customer.",
      paragraphs: [
        "The account encountered searches for recipes, local markets, unrelated products and other businesses. Those terms required review because broad topical relevance does not necessarily reflect intent to buy from this store.",
        "We needed to filter out irrelevant searches while tracking spend, conversions, and attributed revenue.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading: "We reviewed search terms and excluded irrelevant traffic.",
      paragraphs: [
        "We monitored account performance and added negative keywords to exclude recipe queries, local shopping searches, and unrelated suppliers.",
        "We tracked spend, conversions, cost per conversion, and revenue across month-to-date and seven-day periods.",
      ],
      highlights: [
        "We reviewed search terms reaching the account.",
        "We added negative keywords for irrelevant queries.",
        "We monitored advertising spend and attributed revenue.",
        "We reported conversion volume and acquisition cost.",
        "We compared recent results with month-to-date performance.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading:
        "Google Ads reported $14,235.57 in revenue from $1,541.49 in spend.",
      paragraphs: [
        "For August 1–20, 2026, the account reported 923% ROAS and 69 conversions. The report also recorded 1,248 clicks at an average cost of $1.24.",
        "During August 14–20, Google Ads attributed $5,265.05 in revenue to $592.09 in spend, producing 889% ROAS.",
      ],
      followUp:
        "923% ROAS is rounded as reported. Revenue is attributed by Google Ads; product costs and agency fees are not included in this comparison.",
    },
    services: [
      "Google Ads management",
      "Search-term analysis",
      "Negative-keyword management",
      "Conversion reporting",
      "Ecommerce advertising analysis",
    ],
    exhibit: {
      title: "Advertising spend and attributed revenue",
      period: "August 1–20, 2026",
      unit: "Google Ads reporting",
      rows: [
        ["Advertising spend", 1541.49],
        ["Attributed revenue", 14235.57],
      ],
      note: "923% ROAS is rounded as reported. Revenue is attributed by Google Ads; product costs and agency fees are not included in this comparison.",
    },
    relatedServices: [
      {
        href: "/paid-search",
        label: "Ecommerce Paid Search",
      },
      {
        href: "/google-shopping-management",
        label: "Google Shopping Management",
      },
    ],
    path: "/case-study/lummi-island-wild",
    cta: {
      heading: "Ready to discuss your ecommerce goals?",
      description:
        "Tell us where your store or marketing needs attention. We can help define the work and the measures that matter.",
      primaryLabel: "Book a free call",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "r2-manufacturing",
    client: "R2 Manufacturing",
    title:
      "How We Managed Industrial Ecommerce Ads at 697% ROAS for R2 Manufacturing",
    description:
      "We managed R2 Manufacturing’s Google Ads, generating $5,756.75 in attributed revenue from $826.01 in spend at 697% ROAS.",
    metaDescription:
      "We managed R2 Manufacturing’s Google Ads, generating $5,756.75 in attributed revenue from $826.01 in spend at 697% ROAS.",
    stats: [
      {
        value: "697%",
        label: "Google Ads ROAS",
      },
      {
        value: "$5,757",
        label: "Ads-reported revenue",
      },
      {
        value: "$826",
        label: "Advertising spend",
      },
      {
        value: "14.05%",
        label: "Click-through rate",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "A specialized industrial catalog called for precise search intent.",
      paragraphs: [
        "R2 Manufacturing needed paid-search oversight that reflected the equipment its customers were actually looking for. Searches in a technical category can look relevant while referring to different products or applications.",
        "We managed the account, refined negative keywords, and compared rolling 30-day results with month-to-date performance.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "Broad equipment searches could lead the account away from its catalog.",
      paragraphs: [
        "Search-term review surfaced queries for competing attachments, unrelated equipment and product specifications outside the intended offer. Those differences matter when the advertising budget is limited.",
        "The task was to keep the account focused on useful demand while monitoring sales attribution. High engagement was a supporting measure rather than the sole definition of success.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading: "We refined search coverage and tracked the return on spend.",
      paragraphs: [
        "We reviewed search terms and added negative keywords to exclude unrelated loader specifications, attachments, and equipment accessories.",
        "We assessed revenue and spend across the July 20–August 18 reporting window, then compared the account’s month-to-date position. This made the review useful for ongoing management rather than a one-time headline.",
      ],
      highlights: [
        "We managed the Google Ads account.",
        "We reviewed product relevance in search queries.",
        "We added targeted negative keywords.",
        "We tracked attributed revenue against spend.",
        "We compared rolling and month-to-date performance.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading: "We managed the account at 697% ROAS over 30 days.",
      paragraphs: [
        "From July 20 through August 18, 2026, Google Ads reported $5,756.75 in revenue from $826.01 in spend. The account received 660 clicks from 4,699 impressions, producing the reported 14.05% click-through rate.",
        "The August 1–19 view recorded $4,125.50 in attributed revenue from $606.22 in spend, or 681% ROAS. Together, the two reporting windows gave the client a practical view of current performance for its specialized catalog.",
      ],
      followUp:
        "697% ROAS is rounded as reported. Revenue is attributed by Google Ads and does not represent profit after product costs and management fees.",
    },
    services: [
      "Industrial ecommerce paid search",
      "Google Ads account management",
      "Search-term analysis",
      "Negative-keyword management",
      "Advertising performance reporting",
    ],
    exhibit: {
      title: "A focused budget in a technical category",
      period: "July 20–August 18, 2026",
      unit: "Google Ads reporting",
      rows: [
        ["Advertising spend", 826.01],
        ["Attributed revenue", 5756.75],
      ],
      note: "697% ROAS is rounded as reported. Revenue is attributed by Google Ads and does not represent profit after product costs and management fees.",
    },
    relatedServices: [
      {
        href: "/paid-search",
        label: "Ecommerce Paid Search",
      },
      {
        href: "/b2b-ecommerce-agency",
        label: "B2B Ecommerce Agency",
      },
    ],
    path: "/case-study/r2-manufacturing",
    cta: {
      heading: "Ready to discuss your ecommerce goals?",
      description:
        "Tell us where your store or marketing needs attention. We can help define the work and the measures that matter.",
      primaryLabel: "Book a free call",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "the-chefs-garden",
    client: "The Chef’s Garden",
    title:
      "How We Helped The Chef’s Garden Generate $14,863 Through Google Ads in 23 Days",
    description:
      "We managed The Chef’s Garden’s Google Ads, generating $14,863.03 in attributed revenue during June 1–23, 2026.",
    metaDescription:
      "We managed The Chef’s Garden’s Google Ads, generating $14,863.03 in attributed revenue during June 1–23, 2026.",
    stats: [
      {
        value: "$14,863",
        label: "Ads-reported revenue",
      },
      {
        value: "467%",
        label: "Account ROAS",
      },
      {
        value: "560%",
        label: "Build Your Own Box ROAS",
      },
      {
        value: "23 days",
        label: "Reporting window",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading: "Different offers needed their own performance review.",
      paragraphs: [
        "The Chef’s Garden account included a Build Your Own Box campaign and a Performance Max campaign. Each represented a distinct part of the advertising program, making campaign-level reporting useful alongside account totals.",
        "We reviewed account and campaign performance for June 1–23, 2026, refined search-term exclusions, and discussed campaign budgets with the client.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading: "Account averages could obscure campaigns worth discussing.",
      paragraphs: [
        "An overall return figure does not explain the contribution of each campaign. In this case, the client paused two campaigns that had recorded attributed sales during the measured period.",
        "Search traffic also included broad meal ideas, unrelated markets and other suppliers. The work required both ongoing query cleanup and a clear discussion of campaign results before further decisions.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading: "We reviewed each campaign and excluded irrelevant searches.",
      paragraphs: [
        "We reviewed the account and its individual campaigns. We added negative keywords to exclude irrelevant meal searches and unrelated businesses.",
        "We compared Build Your Own Box and Performance Max results separately, then discussed the paused campaigns with the client using their revenue and spend.",
      ],
      highlights: [
        "We managed and reviewed Google Ads performance.",
        "We analyzed Build Your Own Box separately from Performance Max.",
        "We added negative keywords from search-term review.",
        "We reported revenue and cost by campaign.",
        "We discussed paused campaigns using their measured results.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading:
        "The account reported $14,863.03 in revenue during the 23-day window.",
      paragraphs: [
        "From June 1–23, 2026, Google Ads recorded $3,180.73 in spend and 467% overall ROAS. Build Your Own Box contributed $3,303.14 on $589.81 in spend, or 560% ROAS.",
        "Performance Max contributed $5,026.74 in attributed revenue on $1,272.37 in spend, producing 395% ROAS. Other campaigns contributed the remaining $6,533.15.",
      ],
      followUp:
        "We used campaign-level results to help the client assess where to allocate advertising spend.",
    },
    services: [
      "Google Ads management",
      "Performance Max management",
      "Campaign-level analysis",
      "Search-term review",
      "Negative-keyword management",
      "Client performance reporting",
    ],
    exhibit: {
      title: "Campaign results within the account",
      period: "June 1–23, 2026",
      unit: "Google Ads-attributed revenue",
      rows: [
        ["Build Your Own Box", 3303.14],
        ["Performance Max", 5026.74],
        ["Other account campaigns", 6533.15],
      ],
      note: "Revenue uses Google Ads attribution. The three campaign groups total $14,863.03.",
    },
    relatedServices: [
      {
        href: "/google-shopping-management",
        label: "Google Shopping Management",
      },
      {
        href: "/paid-search",
        label: "Ecommerce Paid Search",
      },
    ],
    path: "/case-study/the-chefs-garden",
    cta: {
      heading: "Ready to discuss your ecommerce goals?",
      description:
        "Tell us where your store or marketing needs attention. We can help define the work and the measures that matter.",
      primaryLabel: "Book a free call",
      primaryHref: "/contact-us",
    },
  },
  {
    slug: "milian-truck-center",
    client: "Milian Truck Center",
    title: "How We Supported Milian Truck Center Across Paid Search and Email",
    description:
      "We managed paid search and Klaviyo for Milian Truck Center, achieving 514% Google Ads ROAS in August 2026 and 160% email revenue growth in October 2024.",
    metaDescription:
      "We managed paid search and Klaviyo for Milian Truck Center, achieving 514% Google Ads ROAS in August 2026 and 160% email revenue growth in October 2024.",
    stats: [
      {
        value: "514%",
        label: "Google Ads ROAS",
      },
      {
        value: "$5,091",
        label: "Ads revenue",
      },
      {
        value: "$2,065",
        label: "Email revenue",
      },
      {
        value: "+160%",
        label: "Email revenue",
      },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "Truck-parts ecommerce required support across acquisition and follow-up.",
      paragraphs: [
        "Milian Truck Center’s BigCommerce business needed ongoing marketing for a specialized catalog. Paid search reached shoppers with active demand, while Klaviyo supported email communication and lifecycle follow-up.",
        "We supported both channels over time. Our email work generated $2,065.15 in Klaviyo-attributed revenue in October 2024, and our Google Ads management achieved 514% ROAS during August 1–25, 2026.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading:
        "We needed to reach parts buyers and follow up with interested shoppers.",
      paragraphs: [
        "We found searches for used trucks and other vehicles that did not match the parts catalog. We needed to exclude those searches and focus advertising spend on relevant demand.",
        "The email program had a separate task: follow up with shoppers through abandoned-cart and welcome flows while supporting scheduled campaigns. Results needed to remain attributable to the correct channel and period.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading: "We managed each channel around its own customer behavior.",
      paragraphs: [
        "We reviewed paid-search terms, added negative keywords, and tracked revenue against spend. We also adjusted the account’s target ROAS to guide bidding.",
        "We managed abandoned-cart and welcome email flows, produced scheduled campaigns, and reviewed flow and campaign engagement.",
      ],
      highlights: [
        "We managed Google Ads for the parts catalog.",
        "We excluded irrelevant vehicle searches.",
        "We adjusted the account’s target ROAS.",
        "We supported abandoned-cart and welcome email flows.",
        "We produced and reviewed scheduled email campaigns.",
        "We reported channel performance with separate date ranges.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading:
        "The two reporting periods show measurable results in both channels.",
      paragraphs: [
        "For August 1–25, 2026, Google Ads reported $5,090.57 in revenue from $991.31 in spend, or 514% ROAS. The last seven days of that period recorded 501% ROAS.",
        "In October 2024, our email program generated $2,065.15 in Klaviyo-attributed revenue, approximately 160% more than the previous month.",
      ],
      followUp:
        "We continued to review paid search and email separately, using each channel’s attribution and reporting period.",
    },
    services: [
      "Google Ads management",
      "Target-ROAS adjustments",
      "Search-term analysis",
      "Klaviyo flow management",
      "Email campaign support",
      "BigCommerce marketing support",
      "Channel reporting",
    ],
    exhibit: {
      title: "Paid-search performance for the parts catalog",
      period: "August 1–25, 2026",
      unit: "Google Ads reporting",
      rows: [
        ["Advertising spend", 991.31],
        ["Attributed revenue", 5090.57],
      ],
      note: "Revenue uses Google Ads attribution for August 1–25, 2026.",
    },
    relatedServices: [
      {
        href: "/klaviyo-email-marketing-agency",
        label: "Klaviyo Email Marketing",
      },
      {
        href: "/bigcommerce-support-maintenance",
        label: "BigCommerce Support",
      },
      {
        href: "/paid-search",
        label: "Ecommerce Paid Search",
      },
    ],
    path: "/case-study/milian-truck-center",
    cta: {
      heading: "Ready to discuss your ecommerce goals?",
      description:
        "Tell us where your store or marketing needs attention. We can help define the work and the measures that matter.",
      primaryLabel: "Book a free call",
      primaryHref: "/contact-us",
    },
  },
];
